import { ClipboardModule } from '@angular/cdk/clipboard';
import { DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ButtonComponent } from '@modules/components/button/button.component';
import { ConfirmDialogComponent } from '@modules/components/confirm-dialog/confirm-dialog.component';
import { injectDialog } from '@modules/components/dialog/inject-dialog';
import { PaginatorComponent } from '@modules/components/paginator/paginator.component';
import { SearchFieldComponent } from '@modules/components/search-field/search-field.component';
import { TABLE_DIRECTIVES } from '@modules/components/table/table';
import { TooltipDirective } from '@modules/components/tooltip/tooltip.directive';
import { SaveHeightDirective } from '@modules/directives/save-height.directive';
import { TablePageDirective } from '@modules/directives/table-page.directive';
import { CustomDateFormatPipe } from '@modules/pipes/custom-date-format.pipe';
import { FullNamePipe } from '@modules/pipes/full-name.pipe';
import { spinner } from '@modules/spinner/spinner';
import { PostRequest } from '@type/request.type';
import { UsersList, UserType } from '@type/user.type';
import { splitSearchWords } from '@utils/search';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { InviteDialogUserComponent } from '../invite-dialog-user/invite-dialog-user.component';
import { CopyUserSettingsDialogComponent } from './components/copy-user-settings-dialog/copy-user-settings-dialog.component';
import { UsersService } from './services/users/users.service';
import { capitalize } from '@utils/string';

const DISPLAYED_COLUMNS = ['first_name', 'created_at', 'last_sign_in_at', 'role', 'status', 'actions'] as Array<
  keyof UserType | 'actions'
>;

@Component({
  selector: 'app-users-list',
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    TABLE_DIRECTIVES,
    MatIconModule,
    MatToolbarModule,
    PaginatorComponent,
    SaveHeightDirective,
    ClipboardModule,
    FullNamePipe,
    CustomDateFormatPipe,
    SearchFieldComponent,
    ButtonComponent,
    TooltipDirective,
  ],
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersListComponent extends TablePageDirective<UserType> {
  private dialog = injectDialog();

  private usersApiService = inject(UsersService);

  displayedColumns = computed(() =>
    this.company()?.is_sso_used ? DISPLAYED_COLUMNS.filter((c) => c !== 'actions') : DISPLAYED_COLUMNS,
  );

  canCopySettings = computed(() => !!this.company()?.id);

  tableHeaderList: Record<string, string> = {
    first_name: 'Name',
    created_at: 'Created',
    last_sign_in_at: 'Logged',
    role: 'Role',
    status: 'Status',
  };

  @spinner({ selectorById: 'spinner-container' })
  protected makeRequest(request: PostRequest): Observable<UsersList> {
    return this.usersApiService.getUsersList(request, this.company()?.id).pipe(
      map(({ data, length }) => {
        this.length.set(length);
        return data;
      }),
    );
  }

  protected override triggerUpdate(): number {
    return this.usersApiService.updateUsersTrigger();
  }

  protected override buildQuery(): Record<string, unknown> {
    const search = this.search();
    if (this.company()?.id) {
      return { first_name_or_last_name_or_email_cont: search };
    }
    return { full_name_i_cont_all: splitSearchWords(search) };
  }

  openCopySettingsDialog(): void {
    this.dialog.open(CopyUserSettingsDialogComponent, {
      width: '500px',
      closeOnNavigation: true,
      data: { companyId: this.company()!.id },
    });
  }

  openDialogInviteUser(user?: UserType): void {
    if (user) {
      this.usersApiService
        .getUserById(user.id, this.company()?.id)
        .pipe(take(1))
        .subscribe((fullUser) => this.#openInviteDialog(fullUser));
    } else {
      this.#openInviteDialog();
    }
  }

  #openInviteDialog(user?: UserType): void {
    this.dialog.open(InviteDialogUserComponent, {
      width: '800px',
      height: 'fit-content',
      closeOnNavigation: true,
      data: {
        form: user,
        title: user ? 'Edit member' : 'Invite a new member ',
      },
    });
  }

  showConfirmResetModal(user: UserType): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      closeOnNavigation: true,
      data: {
        content: 'Are you sure you want to reset the password for the user',
        item: { name: user.first_name },
        title: 'Reset',
      },
    });
    this.resetUserPassword(dialogRef, user);
  }

  async suspendUser(user: UserType, method: string): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      closeOnNavigation: true,
      data: {
        content: `Are you sure you want to ${method} user`,
        item: { name: user.fullName },
        title: `${capitalize(method)} user`,
      },
    });
    this.toggleUserStatus(dialogRef, user, method);
  }

  private toggleUserStatus(
    dialogRef: DialogRef<unknown, ConfirmDialogComponent>,
    user: UserType,
    method: string,
  ): void {
    dialogRef.closed
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => this.usersApiService.suspendUser(user, method, this.company()?.id)),
      )
      .subscribe();
  }

  private resetUserPassword(dialogRef: DialogRef<unknown, ConfirmDialogComponent>, user: UserType): void {
    dialogRef.closed
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => this.usersApiService.resetPassword(user, this.company()?.id)),
      )
      .subscribe();
  }
}
